import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const KIOO_LOGO = "https://customer-assets.emergentagent.com/job_ab37571b-81ea-4716-830b-4dd3875c42b0/artifacts/3n0kpvn_KIOO%20RADIO.png";

const MobileScanner = () => {
  // States
  const [currentStep, setCurrentStep] = useState('capture'); // capture, edit, review, details, submit, success
  const [capturedImage, setCapturedImage] = useState(null);
  const [editedImage, setEditedImage] = useState(null); // Edited/cropped image
  const [imageFile, setImageFile] = useState(null);
  const [allReceiptFiles, setAllReceiptFiles] = useState([]); // Multiple files support
  const [isPdfFile, setIsPdfFile] = useState(false); // PDF flag
  const [scanning, setScanning] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [showManualEdit, setShowManualEdit] = useState(false); // Manual edit toggle
  const [cameraReady, setCameraReady] = useState(false);
  
  // Image editor states
  const [editorSettings, setEditorSettings] = useState({
    brightness: 0,
    contrast: 0,
    rotation: 0,
    cropMode: false
  });
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 100, height: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  
  // Form data
  const [formData, setFormData] = useState({
    submitterName: '',
    submitterPhone: '',
    department: '',
    recordType: 'expense', // expense or income
    category: '',
    amount: '',
    currency: 'USD',
    vendorName: '',
    date: new Date().toISOString().split('T')[0],
    paymentMethod: '',
    notes: ''
  });

  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const editorCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const cropImageRef = useRef(null);
  const cropContainerRef = useRef(null);

  // Categories
  const expenseCategories = [
    'Salaries', 'Volunteer Payments', 'Purchases / Supplies', 'Travel & Transport',
    'Fuel', 'Maintenance / Repairs', 'Internet / Starlink', 'Utilities',
    'Training', 'Projects', 'Other'
  ];

  const incomeCategories = [
    'Program Sponsorships', 'Airtime / Advertising', 'Donations', 'Tithes & Offerings',
    'Partnerships', 'Events', 'Grants', 'Merchandise Sales', 'Equipment Rental', 'Other'
  ];

  const departments = [
    'Administration', 'Programs', 'Engineering', 'Marketing', 'Finance', 'Field Operations', 'Other'
  ];

  const paymentMethods = [
    'Cash', 'Bank Transfer', 'Mobile Money - Orange', 'Mobile Money - Lonestar',
    'Mobile Money - MTN', 'Check', 'Wire Transfer', 'Online Payment', 'Other'
  ];

  const currencies = [
    { code: 'USD', name: 'US Dollar', flag: '🇺🇸' },
    { code: 'LRD', name: 'Liberian Dollar', flag: '🇱🇷' },
    { code: 'SLL', name: 'Sierra Leonean Leone', flag: '🇸🇱' },
    { code: 'GNF', name: 'Guinean Franc', flag: '🇬🇳' },
    { code: 'EUR', name: 'Euro', flag: '🇪🇺' },
    { code: 'GBP', name: 'British Pound', flag: '🇬🇧' }
  ];

  // Online/Offline detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Override manifest for scanner PWA
  useEffect(() => {
    // Find and update the manifest link to use scanner-manifest
    const existingManifest = document.querySelector('link[rel="manifest"]');
    const originalHref = existingManifest?.href;
    
    if (existingManifest) {
      existingManifest.href = '/scanner-manifest.json';
    } else {
      // Create new manifest link if not exists
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = '/scanner-manifest.json';
      document.head.appendChild(link);
    }
    
    // Update apple-mobile-web-app-title
    let appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const originalAppleTitle = appleTitleMeta?.content;
    if (appleTitleMeta) {
      appleTitleMeta.content = 'Kioo Scanner';
    }
    
    // Update page title
    document.title = 'Receipt Scanner - Kioo Radio';
    
    return () => {
      // Restore original manifest when leaving scanner
      if (existingManifest && originalHref) {
        existingManifest.href = originalHref;
      }
      if (appleTitleMeta && originalAppleTitle) {
        appleTitleMeta.content = originalAppleTitle;
      }
    };
  }, []);

  // Hide Emergent badge on scanner page
  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) {
      badge.style.display = 'none';
    }
    // Also try other possible selectors
    document.querySelectorAll('[id*="emergent"], .emergent-badge').forEach(el => {
      el.style.display = 'none';
    });
    
    return () => {
      // Restore badge when leaving scanner
      if (badge) {
        badge.style.display = '';
      }
      document.querySelectorAll('[id*="emergent"], .emergent-badge').forEach(el => {
        el.style.display = '';
      });
    };
  }, []);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Start camera - improved with better error handling
  const startCamera = async () => {
    try {
      setCameraError(null);
      setCameraReady(false);
      
      // Request camera with fallback options
      let stream;
      try {
        // Try back camera first
        stream = await navigator.mediaDevices.getUserMedia({
          video: { 
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920, min: 640 },
            height: { ideal: 1080, min: 480 }
          },
          audio: false
        });
      } catch (backCamError) {
        console.log('Back camera failed, trying front camera:', backCamError);
        // Fallback to any camera
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false
        });
      }
      
      streamRef.current = stream;
      setShowCamera(true);
      
      // Wait for DOM to update, then attach stream
      setTimeout(() => {
        if (videoRef.current && streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play()
              .then(() => {
                setCameraReady(true);
                console.log('Camera ready');
              })
              .catch(e => {
                console.error('Video play error:', e);
                setCameraError('Could not start video playback. Please try again.');
              });
          };
        }
      }, 100);
      
    } catch (error) {
      console.error('Camera error:', error);
      let errorMsg = 'Unable to access camera. ';
      if (error.name === 'NotAllowedError') {
        errorMsg += 'Please allow camera permissions in your browser settings.';
      } else if (error.name === 'NotFoundError') {
        errorMsg += 'No camera found on this device.';
      } else if (error.name === 'NotReadableError') {
        errorMsg += 'Camera is being used by another application.';
      } else {
        errorMsg += 'Please use "Upload from Gallery" instead.';
      }
      setCameraError(errorMsg);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setCameraReady(false);
  };

  // Capture photo from camera - goes to editor first
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    // Convert to blob
    canvas.toBlob((blob) => {
      const file = new File([blob], `receipt_${Date.now()}.jpg`, { type: 'image/jpeg' });
      setImageFile(file);
      setAllReceiptFiles([file]);
      const dataUrl = canvas.toDataURL('image/jpeg');
      setCapturedImage(dataUrl);
      setEditedImage(dataUrl);
      stopCamera();
      // Go to editor step instead of review
      setCurrentStep('edit');
    }, 'image/jpeg', 0.92);
  };

  // Handle file selection - support multiple files and all formats
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Supported formats - expanded
    const supportedExtensions = /\.(jpg|jpeg|png|gif|webp|bmp|tiff|heic|heif|pdf)$/i;
    
    const validFiles = files.filter(file => {
      const isValid = file.type.startsWith('image/') || 
                     file.type === 'application/pdf' ||
                     supportedExtensions.test(file.name);
      return isValid;
    });

    if (validFiles.length === 0) {
      alert('Please select supported file types: Images (JPG, PNG, GIF, WebP, etc.) or PDF');
      return;
    }
    
    // For mobile scanner, we handle the first file for now
    // but store all files for multi-receipt support
    const file = validFiles[0];
    setImageFile(file);
    setAllReceiptFiles(validFiles);
    
    if (file.type === 'application/pdf') {
      // For PDF, show a placeholder - skip editor
      setCapturedImage(null);
      setEditedImage(null);
      setIsPdfFile(true);
      setCurrentStep('review');
    } else {
      setIsPdfFile(false);
      const reader = new FileReader();
      reader.onload = (e) => {
        setCapturedImage(e.target.result);
        setEditedImage(e.target.result);
        // Go to editor step for images
        setCurrentStep('edit');
      };
      reader.readAsDataURL(file);
    }
  };

  // Scan with AI
  const scanReceipt = async () => {
    if (!imageFile) return;
    
    setScanning(true);
    
    try {
      const formData = new FormData();
      formData.append('file', imageFile);

      const response = await fetch(`${BACKEND_URL}/api/finance/receipts/scan`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      
      if (data.success && data.extracted_data) {
        setExtractedData(data.extracted_data);
        
        // Auto-fill form with extracted data
        setFormData(prev => ({
          ...prev,
          vendorName: data.extracted_data.vendor_name || '',
          amount: data.extracted_data.total_amount || '',
          currency: data.extracted_data.currency || 'USD',
          date: data.extracted_data.date || prev.date,
          paymentMethod: data.extracted_data.payment_method || '',
          category: data.extracted_data.category_suggestion || '',
          notes: data.extracted_data.notes || ''
        }));
      }
      
      setCurrentStep('details');
    } catch (error) {
      console.error('Scan error:', error);
      alert('Failed to scan receipt. Please try again or fill in details manually.');
      setCurrentStep('details');
    }
    
    setScanning(false);
  };

  // Submit to CRM - Enhanced for multiple receipts
  const submitToCRM = async () => {
    // Validate required fields
    if (!formData.submitterName) {
      alert('Please enter your name');
      return;
    }
    if (!formData.amount) {
      alert('Please enter the amount');
      return;
    }

    setSubmitting(true);

    try {
      // Upload all receipt files
      const uploadedUrls = [];
      const filesToUpload = allReceiptFiles.length > 0 ? allReceiptFiles : [imageFile];
      
      for (const file of filesToUpload) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        uploadFormData.append('record_type', formData.recordType);

        const uploadResponse = await fetch(`${BACKEND_URL}/api/finance/upload-receipt`, {
          method: 'POST',
          body: uploadFormData
        });

        const uploadData = await uploadResponse.json();
        
        if (uploadData.success) {
          uploadedUrls.push(uploadData.file_url);
        } else {
          console.error('Failed to upload file:', file.name);
        }
      }
      
      if (uploadedUrls.length === 0) {
        throw new Error('Failed to upload any receipt files');
      }

      // Now submit the record with all receipt URLs
      const recordData = {
        date: formData.date,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        payment_method: formData.paymentMethod,
        notes: `[Mobile Scanner Submission]\nSubmitted by: ${formData.submitterName}\nPhone: ${formData.submitterPhone || 'N/A'}\nDepartment: ${formData.department || 'N/A'}\nReceipts: ${uploadedUrls.length} file(s)\n\n${formData.notes}`,
        status: 'pending_review', // Always pending for scanner submissions
        submitted_via: 'mobile_scanner',
        submitter_name: formData.submitterName,
        submitter_phone: formData.submitterPhone,
        submitter_department: formData.department,
        receipt_count: uploadedUrls.length,
        all_receipt_urls: uploadedUrls
      };

      if (formData.recordType === 'expense') {
        recordData.category = formData.category;
        recordData.paid_to = formData.vendorName;
        recordData.attachment_url = uploadedUrls[0]; // Primary receipt
        if (uploadedUrls.length > 1) {
          recordData.additional_receipts = uploadedUrls.slice(1);
        }
      } else {
        recordData.source_category = formData.category;
        recordData.payer_name = formData.vendorName;
        recordData.receipt_url = uploadedUrls[0]; // Primary receipt
        if (uploadedUrls.length > 1) {
          recordData.additional_receipts = uploadedUrls.slice(1);
        }
      }

      const endpoint = formData.recordType === 'expense' 
        ? '/api/finance/expenses' 
        : '/api/finance/income';

      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(recordData)
      });

      const data = await response.json();

      if (data.success) {
        setCurrentStep('success');
      } else {
        throw new Error(data.detail || 'Submission failed');
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert('Failed to submit: ' + error.message);
    }

    setSubmitting(false);
  };

  // Reset and start over
  const resetScanner = () => {
    setCapturedImage(null);
    setEditedImage(null);
    setImageFile(null);
    setAllReceiptFiles([]);
    setIsPdfFile(false);
    setExtractedData(null);
    setShowManualEdit(false);
    setEditorSettings({ brightness: 0, contrast: 0, rotation: 0, cropMode: false });
    setCropArea(null);
    setFormData({
      submitterName: formData.submitterName, // Keep name
      submitterPhone: formData.submitterPhone, // Keep phone
      department: formData.department, // Keep department
      recordType: 'expense',
      category: '',
      amount: '',
      currency: 'USD',
      vendorName: '',
      date: new Date().toISOString().split('T')[0],
      paymentMethod: '',
      notes: ''
    });
    setCurrentStep('capture');
  };

  // Apply editor changes and create new file
  const applyEditorChanges = useCallback(() => {
    if (!editorCanvasRef.current || !capturedImage) return;
    
    const canvas = editorCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // If crop mode was used, apply crop
      if (editorSettings.cropMode && cropArea && imageSize.width > 0) {
        // Calculate actual crop coordinates based on image dimensions
        const scaleX = img.width / imageSize.width;
        const scaleY = img.height / imageSize.height;
        
        const cropX = cropArea.x * scaleX;
        const cropY = cropArea.y * scaleY;
        const cropW = cropArea.width * scaleX;
        const cropH = cropArea.height * scaleY;
        
        canvas.width = cropW;
        canvas.height = cropH;
        
        // Apply filters
        const brightness = 1 + editorSettings.brightness / 100;
        const contrast = 1 + editorSettings.contrast / 100;
        ctx.filter = `brightness(${brightness}) contrast(${contrast})`;
        
        ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
      } else {
        // Handle rotation without crop
        const isRotated = editorSettings.rotation === 90 || editorSettings.rotation === 270;
        canvas.width = isRotated ? img.height : img.width;
        canvas.height = isRotated ? img.width : img.height;
        
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((editorSettings.rotation * Math.PI) / 180);
        
        // Apply filters
        const brightness = 1 + editorSettings.brightness / 100;
        const contrast = 1 + editorSettings.contrast / 100;
        ctx.filter = `brightness(${brightness}) contrast(${contrast})`;
        
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        ctx.restore();
      }
      
      // Get edited image
      const editedDataUrl = canvas.toDataURL('image/jpeg', 0.92);
      setEditedImage(editedDataUrl);
      
      // Create new file from edited image
      canvas.toBlob((blob) => {
        if (blob) {
          const editedFile = new File([blob], `edited_receipt_${Date.now()}.jpg`, { type: 'image/jpeg' });
          setImageFile(editedFile);
          setAllReceiptFiles([editedFile]);
        }
      }, 'image/jpeg', 0.92);
    };
    
    img.src = capturedImage;
  }, [capturedImage, editorSettings, cropArea, imageSize]);

  // Auto-enhance for documents
  const autoEnhance = () => {
    setEditorSettings({
      brightness: 15,
      contrast: 25,
      rotation: editorSettings.rotation,
      cropMode: false
    });
  };

  // Rotate image
  const rotateImage = (degrees) => {
    setEditorSettings(prev => ({
      ...prev,
      rotation: (prev.rotation + degrees) % 360
    }));
  };

  // Toggle crop mode
  const toggleCropMode = () => {
    setEditorSettings(prev => ({
      ...prev,
      cropMode: !prev.cropMode
    }));
    // Reset crop area when entering crop mode
    if (!editorSettings.cropMode && imageSize.width > 0) {
      setCropArea({
        x: imageSize.width * 0.1,
        y: imageSize.height * 0.1,
        width: imageSize.width * 0.8,
        height: imageSize.height * 0.8
      });
    }
  };

  // Handle crop area drag
  const handleCropMouseDown = (e, corner) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(corner || 'move');
    const rect = cropContainerRef.current?.getBoundingClientRect();
    if (rect) {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      setDragStart({
        x: clientX - rect.left,
        y: clientY - rect.top,
        cropX: cropArea.x,
        cropY: cropArea.y,
        cropW: cropArea.width,
        cropH: cropArea.height
      });
    }
  };

  const handleCropMouseMove = useCallback((e) => {
    if (!isDragging || !cropContainerRef.current) return;
    
    const rect = cropContainerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const dx = x - dragStart.x;
    const dy = y - dragStart.y;
    
    const minSize = 50;
    
    if (isDragging === 'move') {
      // Move entire crop area
      let newX = dragStart.cropX + dx;
      let newY = dragStart.cropY + dy;
      
      // Constrain to bounds
      newX = Math.max(0, Math.min(newX, imageSize.width - cropArea.width));
      newY = Math.max(0, Math.min(newY, imageSize.height - cropArea.height));
      
      setCropArea(prev => ({ ...prev, x: newX, y: newY }));
    } else if (isDragging === 'se') {
      // Resize from bottom-right corner
      let newW = Math.max(minSize, dragStart.cropW + dx);
      let newH = Math.max(minSize, dragStart.cropH + dy);
      newW = Math.min(newW, imageSize.width - cropArea.x);
      newH = Math.min(newH, imageSize.height - cropArea.y);
      setCropArea(prev => ({ ...prev, width: newW, height: newH }));
    } else if (isDragging === 'nw') {
      // Resize from top-left corner
      let newX = dragStart.cropX + dx;
      let newY = dragStart.cropY + dy;
      let newW = dragStart.cropW - dx;
      let newH = dragStart.cropH - dy;
      
      if (newW >= minSize && newX >= 0) {
        setCropArea(prev => ({ ...prev, x: newX, width: newW }));
      }
      if (newH >= minSize && newY >= 0) {
        setCropArea(prev => ({ ...prev, y: newY, height: newH }));
      }
    }
  }, [isDragging, dragStart, cropArea, imageSize]);

  const handleCropMouseUp = () => {
    setIsDragging(false);
  };

  // Crop event listeners
  useEffect(() => {
    if (editorSettings.cropMode) {
      window.addEventListener('mousemove', handleCropMouseMove);
      window.addEventListener('mouseup', handleCropMouseUp);
      window.addEventListener('touchmove', handleCropMouseMove);
      window.addEventListener('touchend', handleCropMouseUp);
      
      return () => {
        window.removeEventListener('mousemove', handleCropMouseMove);
        window.removeEventListener('mouseup', handleCropMouseUp);
        window.removeEventListener('touchmove', handleCropMouseMove);
        window.removeEventListener('touchend', handleCropMouseUp);
      };
    }
  }, [editorSettings.cropMode, handleCropMouseMove]);

  // Apply crop and exit crop mode
  const applyCrop = () => {
    applyEditorChanges();
    setEditorSettings(prev => ({ ...prev, cropMode: false }));
  };

  // Render capture step
  const renderCaptureStep = () => (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
      {showCamera ? (
        <div className="w-full max-w-lg">
          <div className="relative bg-black rounded-2xl overflow-hidden" style={{ minHeight: '300px' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ minHeight: '300px' }}
            />
            <canvas ref={canvasRef} className="hidden" />
            
            {/* Loading overlay */}
            {!cameraReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                <div className="text-center text-white">
                  <div className="animate-spin text-4xl mb-2">📷</div>
                  <p>Starting camera...</p>
                </div>
              </div>
            )}
            
            {/* Camera overlay */}
            {cameraReady && (
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-8 border-2 border-white/50 rounded-lg"></div>
                <div className="absolute bottom-2 left-0 right-0 text-center text-white text-xs bg-black/30 py-1">
                  Position receipt within frame
                </div>
              </div>
            )}
          </div>
          
          <div className="flex gap-3 mt-4">
            <button
              onClick={stopCamera}
              className="flex-1 py-3 bg-gray-600 text-white rounded-xl font-medium"
            >
              Cancel
            </button>
            <button
              onClick={capturePhoto}
              disabled={!cameraReady}
              className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              📸 Capture
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="text-center mb-8">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-5xl">📸</span>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Scan Your Receipt</h2>
            <p className="text-gray-500">Take a photo or upload from your gallery</p>
          </div>

          {/* Install App Info */}
          <div className="w-full max-w-sm mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-start gap-3">
              <span className="text-2xl">📱</span>
              <div>
                <h3 className="font-bold text-blue-800 text-sm">Install this App</h3>
                <p className="text-blue-600 text-xs mt-1">
                  <strong>Android:</strong> Tap the menu (⋮) → "Add to Home screen"
                </p>
                <p className="text-blue-600 text-xs mt-1">
                  <strong>iPhone:</strong> Tap Share (↑) → "Add to Home Screen"
                </p>
              </div>
            </div>
          </div>

          {cameraError && (
            <div className="w-full max-w-sm mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm">
              {cameraError}
            </div>
          )}

          <div className="w-full max-w-sm space-y-3">
            <button
              onClick={startCamera}
              className="w-full py-4 bg-green-600 text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-3 shadow-lg shadow-green-200 hover:bg-green-700 transition-colors"
            >
              📷 Open Camera
            </button>
            
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full py-4 bg-white border-2 border-green-600 text-green-600 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 hover:bg-green-50 transition-colors"
            >
              🖼️ Upload from Gallery
            </button>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff,.heic,.heif"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </>
      )}
    </div>
  );

  // Render editor step - Image editing/cropping before review
  const renderEditorStep = () => (
    <div className="p-4 pb-32">
      <h2 className="text-lg font-bold text-gray-800 mb-2 text-center">Edit Your Receipt</h2>
      <p className="text-sm text-gray-500 text-center mb-4">
        {editorSettings.cropMode ? 'Drag corners to crop' : 'Crop, rotate, or enhance'}
      </p>
      
      {/* Image Preview with crop overlay */}
      <div 
        ref={cropContainerRef}
        className="bg-gray-100 rounded-2xl p-2 mb-4 relative overflow-hidden"
      >
        <img
          ref={cropImageRef}
          src={editedImage || capturedImage}
          alt="Receipt to edit"
          className="w-full rounded-xl"
          style={{
            filter: editorSettings.cropMode ? 'none' : `brightness(${1 + editorSettings.brightness / 100}) contrast(${1 + editorSettings.contrast / 100})`,
            transform: editorSettings.cropMode ? 'none' : `rotate(${editorSettings.rotation}deg)`
          }}
          onLoad={(e) => {
            setImageSize({
              width: e.target.offsetWidth,
              height: e.target.offsetHeight
            });
            // Initialize crop area
            if (!cropArea.width || cropArea.width === 100) {
              setCropArea({
                x: e.target.offsetWidth * 0.1,
                y: e.target.offsetHeight * 0.1,
                width: e.target.offsetWidth * 0.8,
                height: e.target.offsetHeight * 0.8
              });
            }
          }}
        />
        <canvas ref={editorCanvasRef} className="hidden" />
        
        {/* Crop Overlay */}
        {editorSettings.cropMode && imageSize.width > 0 && (
          <>
            {/* Dark overlay */}
            <div className="absolute inset-2 rounded-xl overflow-hidden pointer-events-none">
              <div className="absolute inset-0 bg-black/50" />
              {/* Clear area for crop */}
              <div 
                className="absolute bg-transparent"
                style={{
                  left: cropArea.x,
                  top: cropArea.y,
                  width: cropArea.width,
                  height: cropArea.height,
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)'
                }}
              />
            </div>
            
            {/* Crop handles */}
            <div
              className="absolute border-2 border-white cursor-move"
              style={{
                left: cropArea.x + 8,
                top: cropArea.y + 8,
                width: cropArea.width,
                height: cropArea.height
              }}
              onMouseDown={(e) => handleCropMouseDown(e, 'move')}
              onTouchStart={(e) => handleCropMouseDown(e, 'move')}
            >
              {/* Corner handles */}
              <div
                className="absolute -top-2 -left-2 w-5 h-5 bg-white rounded-full border-2 border-green-500 cursor-nw-resize"
                onMouseDown={(e) => handleCropMouseDown(e, 'nw')}
                onTouchStart={(e) => handleCropMouseDown(e, 'nw')}
              />
              <div
                className="absolute -bottom-2 -right-2 w-5 h-5 bg-white rounded-full border-2 border-green-500 cursor-se-resize"
                onMouseDown={(e) => handleCropMouseDown(e, 'se')}
                onTouchStart={(e) => handleCropMouseDown(e, 'se')}
              />
              {/* Grid lines */}
              <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="border border-white/30" />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Crop Mode Actions */}
      {editorSettings.cropMode ? (
        <div className="mb-4">
          <div className="flex gap-2">
            <button
              onClick={() => setEditorSettings(prev => ({ ...prev, cropMode: false }))}
              className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium"
            >
              ✕ Cancel
            </button>
            <button
              onClick={applyCrop}
              className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium"
            >
              ✓ Apply Crop
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Quick Actions */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-2 font-medium">Quick Actions</p>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={toggleCropMode}
                className="flex-1 py-2 px-3 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium flex items-center justify-center gap-1"
              >
                ✂️ Crop
              </button>
              <button
                onClick={autoEnhance}
                className="flex-1 py-2 px-3 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium flex items-center justify-center gap-1"
              >
                ✨ Enhance
              </button>
              <button
                onClick={() => rotateImage(90)}
                className="py-2 px-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium"
              >
                🔄
              </button>
              <button
                onClick={() => {
                  setEditorSettings({ brightness: 0, contrast: 0, rotation: 0, cropMode: false });
                  setEditedImage(capturedImage);
                }}
                className="py-2 px-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium"
              >
                ↩️
              </button>
            </div>
          </div>

          {/* Adjustment Sliders */}
          <div className="mb-4 space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">☀️ Brightness</span>
                <span className="text-gray-500">{editorSettings.brightness}</span>
              </div>
              <input
                type="range"
                min="-50"
                max="50"
                value={editorSettings.brightness}
                onChange={(e) => setEditorSettings(prev => ({ ...prev, brightness: parseInt(e.target.value) }))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
              />
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">◐ Contrast</span>
                <span className="text-gray-500">{editorSettings.contrast}</span>
              </div>
              <input
                type="range"
                min="-50"
                max="50"
                value={editorSettings.contrast}
                onChange={(e) => setEditorSettings(prev => ({ ...prev, contrast: parseInt(e.target.value) }))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
              />
            </div>
          </div>

          {/* Preset Modes */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-2 font-medium">Presets</p>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {[
                { id: 'original', name: 'Original', icon: '📷', b: 0, c: 0 },
                { id: 'document', name: 'Document', icon: '📄', b: 15, c: 30 },
                { id: 'receipt', name: 'Receipt', icon: '🧾', b: 20, c: 40 },
                { id: 'bw', name: 'B&W', icon: '⬛', b: 10, c: 50 },
                { id: 'vivid', name: 'Vivid', icon: '🌈', b: 10, c: 20 }
              ].map(preset => (
                <button
                  key={preset.id}
                  onClick={() => setEditorSettings(prev => ({ ...prev, brightness: preset.b, contrast: preset.c }))}
                  className="flex-shrink-0 py-2 px-3 bg-gray-100 hover:bg-green-100 rounded-lg text-sm flex flex-col items-center gap-1 min-w-[70px]"
                >
                  <span className="text-lg">{preset.icon}</span>
                  <span className="text-xs text-gray-600">{preset.name}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Action Buttons - Fixed at bottom (only show when not in crop mode) */}
      {!editorSettings.cropMode && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg z-50">
          <div className="flex gap-3 max-w-lg mx-auto">
            <button
              onClick={resetScanner}
              className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium"
            >
              ↩️ Retake
            </button>
            <button
              onClick={() => {
                applyEditorChanges();
                setCurrentStep('review');
              }}
              className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
            >
              ✓ Done Editing
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Render review step - Enhanced with multiple receipts & PDF support
  const renderReviewStep = () => (
    <div className="p-4">
      <h2 className="text-lg font-bold text-gray-800 mb-4 text-center">Review Your Receipt{allReceiptFiles.length > 1 ? 's' : ''}</h2>
      
      {/* Main Receipt Preview */}
      <div className="bg-gray-100 rounded-2xl p-2 mb-4">
        {isPdfFile ? (
          <div className="flex flex-col items-center justify-center py-8 bg-red-50 rounded-xl">
            <span className="text-6xl mb-3">📄</span>
            <p className="text-gray-700 font-medium">{imageFile?.name}</p>
            <p className="text-sm text-gray-500 mt-1">PDF Document</p>
            <p className="text-xs text-amber-600 mt-2">⚠️ AI scanning not available for PDFs</p>
          </div>
        ) : (
          <img
            src={editedImage || capturedImage}
            alt="Captured receipt"
            className="w-full rounded-xl"
          />
        )}
      </div>

      {/* Edit Image Button */}
      {!isPdfFile && (
        <button
          onClick={() => setCurrentStep('edit')}
          className="w-full mb-4 py-2 bg-purple-100 text-purple-700 rounded-xl font-medium flex items-center justify-center gap-2"
        >
          🖌️ Edit Image
        </button>
      )}

      {/* Multiple Files Indicator */}
      {allReceiptFiles.length > 1 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-blue-700 text-sm flex items-center gap-2">
            <span>📎</span>
            <span><strong>{allReceiptFiles.length} files</strong> selected for this transaction</span>
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {allReceiptFiles.map((file, idx) => (
              <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                {file.name.length > 15 ? file.name.substring(0, 15) + '...' : file.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Add More Receipts Button */}
      <div className="mb-4">
        <label className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium flex items-center justify-center gap-2 cursor-pointer border-2 border-dashed border-gray-300 hover:border-green-400 hover:bg-green-50 transition-colors">
          <span>➕</span> Add More Receipts
          <input
            type="file"
            accept="image/*,.pdf,.jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff,.heic,.heif"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files.length > 0) {
                const newFiles = Array.from(e.target.files);
                setAllReceiptFiles([...allReceiptFiles, ...newFiles]);
              }
            }}
          />
        </label>
      </div>

      <div className="flex gap-3">
        <button
          onClick={resetScanner}
          className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium"
        >
          ↩️ Retake
        </button>
        <button
          onClick={scanReceipt}
          disabled={scanning || isPdfFile}
          className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {scanning ? (
            <>
              <span className="animate-spin">⚡</span>
              Scanning...
            </>
          ) : (
            <>
              🤖 Scan with AI
            </>
          )}
        </button>
      </div>
      
      <button
        onClick={() => setCurrentStep('details')}
        className="w-full mt-3 py-2 text-gray-500 text-sm"
      >
        {isPdfFile ? 'Continue to enter details →' : 'Skip AI scan, enter details manually →'}
      </button>
    </div>
  );

  // Render details step - Enhanced with manual edit toggle
  const renderDetailsStep = () => (
    <div className="p-4 pb-24">
      <h2 className="text-lg font-bold text-gray-800 mb-4">Enter Details</h2>

      {extractedData && extractedData.confidence_score > 0.5 && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex items-center justify-between">
            <p className="text-green-700 text-sm flex items-center gap-2">
              <span>✅</span>
              AI extracted data with {Math.round(extractedData.confidence_score * 100)}% confidence
            </p>
            <button
              onClick={() => setShowManualEdit(!showManualEdit)}
              className="text-xs text-green-600 font-medium underline"
            >
              {showManualEdit ? 'Hide' : 'Edit AI Data'}
            </button>
          </div>
          
          {/* Expandable AI Data Editor */}
          {showManualEdit && (
            <div className="mt-3 pt-3 border-t border-green-200 space-y-2">
              <p className="text-xs text-gray-600 mb-2">✏️ Correct any errors from AI extraction:</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500">Vendor</label>
                  <input
                    type="text"
                    value={formData.vendorName}
                    onChange={(e) => setFormData({...formData, vendorName: e.target.value})}
                    className="w-full px-2 py-1.5 text-sm border rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    className="w-full px-2 py-1.5 text-sm border rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full px-2 py-1.5 text-sm border rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Payment Method</label>
                  <select
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                    className="w-full px-2 py-1.5 text-sm border rounded-lg bg-white"
                  >
                    <option value="">Select...</option>
                    {paymentMethods.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Multiple receipts indicator */}
      {allReceiptFiles.length > 1 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-blue-700 text-sm">
            📎 <strong>{allReceiptFiles.length} receipts</strong> will be attached to this record
          </p>
        </div>
      )}

      {/* Mini preview */}
      <div className="mb-4 flex gap-3 items-start">
        {isPdfFile ? (
          <div className="w-20 h-20 bg-red-50 rounded-lg flex flex-col items-center justify-center border border-red-200">
            <span className="text-2xl">📄</span>
            <span className="text-xs text-red-600 mt-1">PDF</span>
          </div>
        ) : (
          <img
            src={editedImage || capturedImage}
            alt="Receipt"
            className="w-20 h-20 object-cover rounded-lg"
          />
        )}
        <div className="flex-1">
          <p className="text-sm text-gray-500">
            {allReceiptFiles.length > 1 
              ? `${allReceiptFiles.length} receipts captured` 
              : 'Receipt captured'}
          </p>
          <button
            onClick={() => isPdfFile ? resetScanner() : setCurrentStep('edit')}
            className="text-sm text-green-600 font-medium"
          >
            {isPdfFile ? 'Change file' : 'Edit image'}
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-4">
        {/* Your Information */}
        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
          <h3 className="font-semibold text-gray-700 flex items-center gap-2">
            <span>👤</span> Your Information
          </h3>
          
          <div>
            <label className="text-sm text-gray-600 mb-1 block">Your Name *</label>
            <input
              type="text"
              value={formData.submitterName}
              onChange={(e) => setFormData({...formData, submitterName: e.target.value})}
              placeholder="Enter your full name"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600 mb-1 block">Phone Number</label>
            <input
              type="tel"
              value={formData.submitterPhone}
              onChange={(e) => setFormData({...formData, submitterPhone: e.target.value})}
              placeholder="+231..."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600 mb-1 block">Department</label>
            <select
              value={formData.department}
              onChange={(e) => setFormData({...formData, department: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
            >
              <option value="">Select department...</option>
              {departments.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Record Type */}
        <div>
          <label className="text-sm text-gray-600 mb-2 block">Record Type *</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setFormData({...formData, recordType: 'expense', category: ''})}
              className={`py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors ${
                formData.recordType === 'expense'
                  ? 'bg-red-100 text-red-700 border-2 border-red-500'
                  : 'bg-gray-100 text-gray-600 border-2 border-transparent'
              }`}
            >
              📉 Expense
            </button>
            <button
              onClick={() => setFormData({...formData, recordType: 'income', category: ''})}
              className={`py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors ${
                formData.recordType === 'income'
                  ? 'bg-green-100 text-green-700 border-2 border-green-500'
                  : 'bg-gray-100 text-gray-600 border-2 border-transparent'
              }`}
            >
              💰 Income
            </button>
          </div>
        </div>

        {/* Category */}
        <div>
          <label className="text-sm text-gray-600 mb-1 block">Category *</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({...formData, category: e.target.value})}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
          >
            <option value="">Select category...</option>
            {(formData.recordType === 'expense' ? expenseCategories : incomeCategories).map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {/* Amount & Currency */}
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="text-sm text-gray-600 mb-1 block">Amount *</label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({...formData, amount: e.target.value})}
              placeholder="0.00"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div>
            <label className="text-sm text-gray-600 mb-1 block">Currency</label>
            <select
              value={formData.currency}
              onChange={(e) => setFormData({...formData, currency: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
            >
              {currencies.map(c => (
                <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Vendor/Payer */}
        <div>
          <label className="text-sm text-gray-600 mb-1 block">
            {formData.recordType === 'expense' ? 'Paid To / Vendor' : 'Received From / Payer'}
          </label>
          <input
            type="text"
            value={formData.vendorName}
            onChange={(e) => setFormData({...formData, vendorName: e.target.value})}
            placeholder={formData.recordType === 'expense' ? 'Vendor name' : 'Payer name'}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>

        {/* Date */}
        <div>
          <label className="text-sm text-gray-600 mb-1 block">Date</label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({...formData, date: e.target.value})}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>

        {/* Payment Method */}
        <div>
          <label className="text-sm text-gray-600 mb-1 block">Payment Method</label>
          <select
            value={formData.paymentMethod}
            onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
          >
            <option value="">Select method...</option>
            {paymentMethods.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        {/* Notes */}
        <div>
          <label className="text-sm text-gray-600 mb-1 block">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({...formData, notes: e.target.value})}
            rows={3}
            placeholder="Any additional notes..."
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
      </div>

      {/* Submit Button - Fixed at bottom with high z-index */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg z-[9999]">
        <div className="max-w-lg mx-auto">
          <button
            onClick={submitToCRM}
            disabled={submitting || !formData.submitterName || !formData.amount}
            className="w-full py-4 bg-green-600 text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-200"
          >
            {submitting ? (
              <>
                <span className="animate-spin">⚡</span>
                Submitting...
              </>
            ) : (
              <>
                📤 Submit for Review
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Render success step
  const renderSuccessStep = () => (
    <div className="flex flex-col items-center justify-center min-h-[70vh] p-4 text-center">
      <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6">
        <span className="text-5xl">✅</span>
      </div>
      
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Submitted Successfully!</h2>
      <p className="text-gray-500 mb-8 max-w-sm">
        Your receipt has been submitted and is pending review by the finance team.
      </p>

      <div className="w-full max-w-sm bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
        <p className="text-amber-700 text-sm flex items-start gap-2">
          <span>⏳</span>
          <span>Your submission is <strong>pending approval</strong>. You will be notified once it is reviewed.</span>
        </p>
      </div>

      <button
        onClick={resetScanner}
        className="w-full max-w-sm py-4 bg-green-600 text-white rounded-xl font-semibold text-lg shadow-lg shadow-green-200"
      >
        📸 Scan Another Receipt
      </button>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Receipt Scanner - Kioo Radio</title>
        <meta name="description" content="Scan and submit receipts to Kioo Radio CRM" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
        <meta name="theme-color" content="#059669" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <link rel="manifest" href="/scanner-manifest.json" />
        {/* Hide Emergent badge on scanner app */}
        <style>{`
          #emergent-badge { display: none !important; }
          .emergent-badge { display: none !important; }
          [id*="emergent"] { display: none !important; }
        `}</style>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-gradient-to-r from-green-600 to-green-700 text-white sticky top-0 z-40 shadow-lg">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={KIOO_LOGO} alt="Kioo Radio" className="h-10 w-10 rounded-full bg-white p-1" />
              <div>
                <h1 className="font-bold text-lg">Kioo Radio</h1>
                <p className="text-green-200 text-xs">Receipt Scanner</p>
              </div>
            </div>
            
            {/* Online/Offline indicator */}
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
              isOnline ? 'bg-green-500/30 text-white' : 'bg-red-500/30 text-red-100'
            }`}>
              <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-white' : 'bg-red-300 animate-pulse'}`}></span>
              {isOnline ? 'Online' : 'Offline'}
            </div>
          </div>

          {/* Progress Steps - Updated to include edit step */}
          <div className="px-4 pb-3">
            <div className="flex items-center justify-between max-w-xs mx-auto">
              {['capture', 'edit', 'review', 'details', 'success'].map((step, index) => {
                const isActive = currentStep === step;
                const stepOrder = ['capture', 'edit', 'review', 'details', 'success'];
                const isPast = stepOrder.indexOf(currentStep) > index;
                
                // Skip showing edit step number if we went directly to review (PDF)
                if (step === 'edit' && isPdfFile) return null;
                
                const displayIndex = isPdfFile && index > 0 ? index : index + 1;
                
                return (
                  <React.Fragment key={step}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                      isActive ? 'bg-white text-green-600' : 
                      isPast ? 'bg-green-400 text-white' : 
                      'bg-green-500/30 text-green-200'
                    }`}>
                      {isPast ? '✓' : displayIndex}
                    </div>
                    {index < 4 && !(step === 'edit' && isPdfFile) && (
                      <div className={`flex-1 h-1 mx-1 rounded ${
                        isPast ? 'bg-green-400' : 'bg-green-500/30'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </header>

        {/* Offline Banner */}
        {!isOnline && (
          <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm">
            📴 You are offline. Submissions will be saved and sent when you are back online.
          </div>
        )}

        {/* Main Content */}
        <main className="max-w-lg mx-auto pb-20">
          {currentStep === 'capture' && renderCaptureStep()}
          {currentStep === 'edit' && renderEditorStep()}
          {currentStep === 'review' && renderReviewStep()}
          {currentStep === 'details' && renderDetailsStep()}
          {currentStep === 'success' && renderSuccessStep()}
        </main>

        {/* Footer - Only show on capture and success steps */}
        {(currentStep === 'capture' || currentStep === 'success') && (
          <footer className="fixed bottom-0 left-0 right-0 bg-white border-t py-2 px-4 text-center text-xs text-gray-400 z-30">
            Kioo Radio © 2025 • Spreading the Gospel
          </footer>
        )}
      </div>
    </>
  );
};

export default MobileScanner;
